import { ReactElement, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { FranchiseModel } from "../../../data/entity/franchise-model";
import { makeSendContact } from "../../../main/helper/factories/send-contact-factory";
import { FranchiseContext } from "../../context/franchise-context/franchise-context";
import { FooterAddress } from "../footer/footer-address";
import { FooterSimple } from "../footer/footer-simple";
import { FranchiseError } from "../franchise/franchise-error";
import { Header } from "../header/header";
import { Loading } from "../loading/loading";
import { AboutMore } from "../sections/about-more/about";
import { About } from "../sections/about/about";
import { Banner } from "../sections/banner/banner";
import { Clinic } from "../sections/clinic/clinic";
import { Contact } from "../sections/contact/contact";
import { Faq } from "../sections/faq/faq";
import { Hero } from "../sections/hero/hero";
import { LeadForm } from "../sections/lead-form/lead-form";
import { Partner } from "../sections/partner/partner";
import { PlainText } from "../sections/plain-text/plain-text";
import { Price } from "../sections/price/price";
import { Testimonials } from "../sections/testimonials/testimonials";
import { WhatsAppLinkButton } from "../whatsapp-link-button/whatsapp-link-button";
import { HomeProps } from "./home-props";
import * as S from "./home-styles";

export enum PageState {
  LOADING,
  READY,
  NO_CONTENT,
  ERROR,
}

export enum LandingpageType {
  PHYSICAL = "PHYSICAL",
  DIGITAL = "DIGITAL",
}

const sendContact = makeSendContact();

export function Home({
  loadFranchise,
  apiUrl,
  urlBuilder,
}: HomeProps): ReactElement {
  const [state, setState] = useState<PageState>(PageState.LOADING);
  const [data, setData] = useState<FranchiseModel>(
    {} as unknown as FranchiseModel,
  );
  const [params, setParams] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      setState(PageState.LOADING);
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const linkParams = Object.fromEntries(urlParams.entries());
      const response = await loadFranchise.handle();

      switch (response.status) {
        case 200: {
          // `params` so vem no corpo do 200. Ler antes do switch quebrava a
          // pagina no 204 e deixava o estado preso em LOADING.
          const franchiseParams = (response.data.params ?? []).reduce(
            (acc: Record<string, string>, val) => {
              acc[val.param] = val.value;
              return acc;
            },
            {},
          );
          setParams(Object.assign(franchiseParams, linkParams));
          setData(response.data);
          setState(PageState.READY);
          break;
        }
        case 204:
          setState(PageState.NO_CONTENT);
          break;
        default:
          setState(PageState.ERROR);
      }
    })();
  }, [loadFranchise]);

  if (state === PageState.LOADING) return <Loading />;
  // Dominio sem franquia ativa: o DNS pode continuar apontando para ca por um
  // tempo apos a desativacao, entao a landing page cede lugar ao 404.
  if (state === PageState.NO_CONTENT) return <Navigate to="/404" replace />;
  if (state === PageState.ERROR) return <FranchiseError />;
  return (
    <FranchiseContext.Provider value={data}>
      <Header params={params} urlBuilder={urlBuilder} />
      <S.Container>
        {data.sections.map((section) => {
          switch (section.type) {
            case "HERO":
              return (
                <Hero
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "BANNER":
              return (
                <Banner
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "PRICE":
              return (
                <Price
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "ABOUT_CARTAO_DE_TODOS":
              return (
                <About
                  key={section.id + "_" + section.name}
                  {...section}
                  mainLink={data.mainLink}
                  apiUrl={apiUrl}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "ABOUT_CARTAO_DE_TODOS_MORE":
              return (
                <AboutMore
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "PARTNER":
              return (
                <Partner
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "CLINICS":
              return (
                <Clinic
                  key={section.id + "_" + section.name}
                  {...section}
                  mainLink={data.mainLink}
                  apiUrl={apiUrl}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "PLAIN_TEXT":
              return (
                <PlainText
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "TESTIMONIALS":
              return (
                <Testimonials
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "CONTACT":
              return (
                <Contact
                  key={section.id + "_" + section.name}
                  sendContact={sendContact}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "FAQ":
              return (
                <Faq
                  key={section.id + "_" + section.name}
                  {...section}
                  params={params}
                  urlBuilder={urlBuilder}
                />
              );
            case "LEAD_FORM":
              return (
                <LeadForm
                  key={section.id + "_" + section.name}
                  id={section.id}
                  params={params}
                  values={section.values}
                  franchiseData={data}
                  franchiseDomain={window.location.hostname}
                />
              );
            default:
              <>{section.type}</>;
          }
          return <></>;
        })}
      </S.Container>
      {data.footer.showWhatsappButton && (
        <WhatsAppLinkButton phone={data.footer.whatsapp} />
      )}
      {data.type === LandingpageType.PHYSICAL && (
        <FooterAddress {...data.footer} />
      )}
      {data.type === LandingpageType.DIGITAL && (
        <FooterSimple {...data.footer} />
      )}
    </FranchiseContext.Provider>
  );
}
